import {
  FdCloseNaked,
  FdInfoBold,
  FdSanityCheckInfo,
  FdWarningDanger,
} from '@qodly/icons';
import cn from 'classnames';
import Markdown from 'markdown-to-jsx';
import React, { FC } from 'react';

export type ToastKind =
  | 'warning'
  | 'info'
  | 'danger'
  | 'message'
  | 'error'
  | 'multi';

export type ToastCustomIcon = FC<{
  className?: string;
}>;

interface IToastProps {
  kind: ToastKind;
  message: string | IMultilineMessage[];
  markdown?: boolean;
  id?: number | string;
  onClose(): void;
  customIcon?: ToastCustomIcon;
}
export interface IMultilineMessage {
  title?: string;
  messages: string[];
}

interface IToastIconProps {
  kind: ToastKind;
  customIcon?: ToastCustomIcon;
}

const ToastIcon: React.FC<IToastIconProps> = ({ kind, customIcon: Icon }) => {
  if (Icon) {
    return <Icon className="mr-1 h-6 w-6 flex-none" />;
  }
  switch (kind) {
    case 'error':
    case 'danger':
      return <FdSanityCheckInfo className="flex-none h-6 w-6" />;
    case 'info':
      return (
        <>
          <FdInfoBold className="flex-none h-6 w-6" />
        </>
      );
    case 'warning':
      return (
        <>
          <FdWarningDanger className="flex-none h-6 w-6" />
        </>
      );
    case 'message':
      return (
        <>
          <FdInfoBold className="flex-none h-6 w-6" />
        </>
      );
    default:
      return null;
  }
};

export const Toast: React.FC<IToastProps> = ({
  id,
  onClose,
  kind,
  message,
  markdown,
  customIcon,
}) => {
  const renderCloseIcon = ({ className }: { className?: string } = {}) => (
    <button
      onClick={onClose}
      className={cn('flex items-center justify-center p-1', className)}
    >
      <FdCloseNaked className="h-5 w-5 font-thin stroke-current" />
    </button>
  );

  return (
    <div
      key={id}
      className={cn(
        'relative flex items-center rounded-lg py-2 px-4 font-semibold',
        {
          'bg-yellow-100 text-yellow-400': kind === 'warning',
          'bg-red-200 text-red-500': kind === 'danger' || kind === 'error',
          'bg-primary-light text-primary': kind === 'info',
          'bg-green-200 text-green-500': kind === 'message',
        },
      )}
    >
      {typeof message === 'string' ? (
        <div className="w-full flex items-center justify-between gap-x-3">
          <div className="flex items-center gap-1">
            <ToastIcon kind={kind} customIcon={customIcon} />
            {markdown ? <Markdown>{message}</Markdown> : <>{message}</>}
          </div>
          {renderCloseIcon()}
        </div>
      ) : (
        <div className="flex items-start">
          <div className="flex flex-col gap-2 justify-between">
            {message
              .filter((msg) => msg.messages.length > 0)
              .map((msg) => (
                <>
                  {msg.title && <span className="flex items-center gap-1 font-bold capitalize">
                    <ToastIcon kind={kind} customIcon={customIcon} />
                    <span>{msg.title} :</span>
                  </span>}
                  <ul className={cn("list-disc",
                    {
                      "px-12": msg.title,
                      "px-6": !msg.title
                    })}>
                    {msg.messages.map((item) => {
                      return (
                        <li>
                          {markdown ? <Markdown>{item}</Markdown> : <>{item}</>}
                        </li>
                      );
                    })}
                  </ul>
                </>
              ))}
          </div>
          {renderCloseIcon({
            className: 'absolute top-1 right-1',
          })}
        </div>
      )}
    </div>
  );
};
